<template>
  <div class="terms-page-wrapper">
    <Navbar />

    <div class="page-container">
      <div class="terms-card">
        <!-- 標題 -->
        <h1 class="card-title">隱私權政策及會員服務之附加條款</h1>

        <!-- 說明內文 -->
        <p class="card-description">
          該服務將蒐集共通會員註冊資料以外的其他資訊，<br />
          請閱讀並同意以下文件後繼續使用。
        </p>

        <!-- 勾選框 -->
        <div class="checkbox-wrapper">
          <a-checkbox v-model:checked="isAgreed" class="terms-checkbox">
            <span class="checkbox-label">
              我已閱讀並同意 {{ standardName }} 的
              <a href="#" class="terms-link" @click.prevent="openTermsModal">隱私權政策及會員服務之附加條款</a>。
            </span>
          </a-checkbox>
        </div>

        <!-- 按鈕群組 -->
        <div class="button-group">
          <button
            type="button"
            class="btn-continue"
            :class="{ 'is-disabled': !isAgreed || isSubmitting }"
            :disabled="isSubmitting"
            @click="handleContinue"
          >
            <a-spin v-if="isSubmitting" size="small" style="margin-right: 8px;" />
            繼續
          </button>
          <button
            type="button"
            class="btn-cancel"
            :disabled="isSubmitting"
            @click="handleCancel"
          >
            取消
          </button>
        </div>
      </div>
    </div>

    <!-- 附加條款彈窗 -->
    <AdditionalTermsModal
      v-model:open="showModal"
      :standardName="standardName"
      @agree="handleModalAgree"
      @cancel="showModal = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { message } from "ant-design-vue";
import Navbar from "../components/Navbar.vue";
import AdditionalTermsModal from "../components/AdditionalTermsModal.vue";
import { authAPI, contentAPI } from "../services/api";
import { SITE_NAME } from "../constants/site";

const router = useRouter();
const route = useRoute();

const isAgreed = ref(false);
const showModal = ref(false);
const isSubmitting = ref(false);
const latestVersion = ref("v1.0");

const standardName = computed(() => SITE_NAME || "SNOMED CT");

onMounted(async () => {
  try {
    const rawUserData = localStorage.getItem("user_data");
    if (rawUserData) {
      const user = JSON.parse(rawUserData);
      const isAlreadyAgreed = user?.standards_terms_agreed ? user.standards_terms_agreed['snomed_ct'] : user?.additional_terms_agreed;
      if (isAlreadyAgreed) {
        const redirectUrl = route.query.redirect || "/";
        router.replace(redirectUrl);
        return;
      }
    }
  } catch (e) {
    console.error("檢查條款同意狀態失敗:", e);
  }

  // 取得最新條款版號
  try {
    if (contentAPI.getAdditionalTerms) {
      const res = await contentAPI.getAdditionalTerms();
      if (res?.data?.version) {
        latestVersion.value = res.data.version;
      }
    }
  } catch (e) {
    console.warn("取得最新附加條款版號失敗:", e);
  }

  // 自動展開附加條款彈窗方便使用者閱讀
  showModal.value = true;
});

const openTermsModal = () => {
  showModal.value = true;
};

const handleModalAgree = async (version) => {
  if (version) {
    latestVersion.value = version;
  }
  isAgreed.value = true;
  showModal.value = false;
  await handleContinue();
};

const handleContinue = async () => {
  if (!isAgreed.value) {
    message.warning("請先閱讀並勾選同意附加條款");
    return;
  }

  isSubmitting.value = true;
  try {
    const token = localStorage.getItem("ms_platform_token");
    if (!token) {
      message.info("請先登入會員帳號");
      router.push(`/sign-in?redirect=${encodeURIComponent(route.fullPath)}`);
      return;
    }

    const res = await authAPI.agreeAdditionalTerms({
      standard: 'snomed_ct',
      version: latestVersion.value,
    });

    const responsePayload = res?.data?.content || res?.data;
    if (res?.data?.status === "success" || responsePayload?.additional_terms_agreed || res?.data?.additional_terms_agreed) {
      message.success("已成功同意附加條款");

      try {
        const rawUserData = localStorage.getItem("user_data");
        if (rawUserData) {
          const user = JSON.parse(rawUserData);
          user.additional_terms_agreed = true;
          user.additional_terms_version = responsePayload?.additional_terms_version || latestVersion.value;
          user.additional_terms_agreed_at = responsePayload?.additional_terms_agreed_at || new Date().toISOString();
          if (!user.standards_terms_agreed) user.standards_terms_agreed = {};
          user.standards_terms_agreed['snomed_ct'] = true;
          localStorage.setItem("user_data", JSON.stringify(user));
        }
      } catch (e) {
        console.error("更新本地 user_data 失敗:", e);
      }

      const redirectUrl = route.query.redirect || "/";
      router.push(redirectUrl);
    } else {
      message.error(res?.data?.message || "簽署附加條款失敗，請稍後再試");
    }
  } catch (err) {
    console.error("簽署附加條款發生錯誤:", err);
    message.error(err?.response?.data?.message || "簽署附加條款失敗，請確認網路連線或稍後再試");
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push("/");
  }
};
</script>

<style scoped>
.terms-page-wrapper {
  min-height: 100vh;
  background: #eef5fb;
  display: flex;
  flex-direction: column;
}

.page-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 20px 60px;
  box-sizing: border-box;
}

.terms-card {
  background: #ffffff;
  width: 100%;
  max-width: 635px;
  border-radius: 32px;
  padding: 56px 48px;
  box-shadow: 0 10px 40px rgba(10, 43, 94, 0.08);
  text-align: center;
  box-sizing: border-box;
}

.card-title {
  font-size: 32px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 20px;
  letter-spacing: -0.5px;
}

.card-description {
  font-size: 15px;
  color: #475569;
  line-height: 1.7;
  margin-bottom: 36px;
  font-weight: 400;
}

.checkbox-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 40px;
}

.terms-checkbox {
  font-size: 15px;
  color: #1e293b;
}

.checkbox-label {
  font-size: 15px;
  color: #1e293b;
}

.terms-link {
  color: #1e3a8a;
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease;
}

.terms-link:hover {
  color: #2563eb;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.btn-continue {
  width: 100%;
  height: 52px;
  background-color: #0a2552;
  color: #ffffff;
  border: none;
  border-radius: 9999px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.btn-continue:hover:not(:disabled) {
  background-color: #081d42;
}

.btn-continue.is-disabled {
  opacity: 0.85;
}

.btn-cancel {
  width: 100%;
  height: 52px;
  background-color: transparent;
  color: #334155;
  border: 1.5px solid #64748b;
  border-radius: 9999px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel:hover:not(:disabled) {
  background-color: #f8fafc;
  border-color: #334155;
  color: #0f172a;
}

@media (max-width: 640px) {
  .terms-card {
    padding: 36px 24px;
    border-radius: 24px;
  }

  .card-title {
    font-size: 26px;
  }

  .card-description {
    font-size: 14px;
  }

  .checkbox-label {
    font-size: 14px;
  }
}
</style>
